import * as Sentry from "@sentry/angular";
import {Config} from "../../../config";
import {ensureInitializedMessageCode, isBaseMessageCode, isIgnoredError} from "@mindpath/shared";
import {
  isIgnoredRootTransaction,
  isIgnoredSpan,
  isSuccessfulFlushTransaction,
  isThirdPartyTelemetryUrl
} from "./span-filtering";

export * from './sentry.service';

export function InitializeSentry(config: Config) {

  // Derived from config rather than hardcoded, so the check stays tied to the collector we actually
  // configured instead of to whatever happens to look like a Piwik URL.
  const telemetryHosts = (() => {
    try {
      return config.piwik?.url ? [new URL(config.piwik.url).hostname] : [];
    } catch {
      return [];
    }
  })();

  Sentry.init({
    environment: config.environment,
    dsn: config.sentry?.dsn,

    ignoreErrors: [
      'AbortError',
      'The user aborted a request'
    ],
    beforeSend(event, hint) {
      const error = hint?.originalException;
      try {
        // Drop non-actionable noise before fingerprinting. This is the only chokepoint that catches
        // both paths in: `CaptureError` -> `captureException`, and `GlobalErrorHandler` ->
        // `super.handleError`, which bypasses `CaptureError` entirely.
        //
        // Deliberately not handled via `ignoreErrors` — that matches the top-level message, which
        // for our `MapError`-wrapped errors is `[BSR0000]: ...`, so it would silently no-op.
        if (isIgnoredError(error)) {
          return null;
        }

        if (isBaseMessageCode(error) && error.code !== null) {
          const messageCode = ensureInitializedMessageCode(error);
          hint.originalException = messageCode;
          // Not the user message: the API's copy for a code can embed per-request data (ITK0001 names the clinic)
          event.fingerprint = [error.code];
          if (messageCode.devDescription) {
            event.fingerprint.push(messageCode.devDescription);
          }
        }
      } catch (e) {
        console.error('Error checking if event is a MessageCode', e);
      }
      return event;
    },
    beforeSendTransaction(event) {
      try {
        if (isSuccessfulFlushTransaction(event)) {
          return null;
        }
        if (isIgnoredRootTransaction(event)) {
          return null;
        }
        // Filtering here rather than via `beforeSendSpan`: that hook logs a deprecation warning when
        // it drops a span and is removed in v9, while mutating `event.spans` gets identical
        // dropped-span accounting (baseclient.js:806-816).
        if (event.spans) {
          event.spans = event.spans.filter(span => !isIgnoredSpan(span));
        }
      } catch (e) {
        console.error('Error filtering transaction', e);
      }
      return event;
    },
    integrations: [
      Sentry.extraErrorDataIntegration({
        depth: 8,
        captureErrorCause: true,
      }),
      Sentry.captureConsoleIntegration({levels: []}),
      Sentry.browserTracingIntegration({
        traceFetch: true,
        // Piwik's own beacons are not our telemetry to trace. `resource.*` filtering already covers
        // its loader script and any `sendBeacon`/img beacon; this closes the XHR/fetch gap. Trace
        // headers are unaffected — `shouldCreateSpan` and `shouldAttachHeaders` are independent
        // (request.js:34-37), and Piwik was never in `tracePropagationTargets` anyway.
        shouldCreateSpanForRequest: url => !isThirdPartyTelemetryUrl(url, telemetryHosts),
        // Both produce spans we discard. Disabled at the source rather than filtered: `ui.interaction.click`
        // is an INP standalone span sent in its own envelope, so it never reaches `beforeSendTransaction`,
        // and `beforeSendSpan` — the only hook that does see it — is deprecated for dropping and removed in
        // v9. Cost: Sentry no longer gets the INP web vital, since that span is the measurement.
        enableInp: false,
        _experiments: {
          enableInteractions: false,
          // NB: standalone CLS spans skip `captureEvent`, so they would bypass `beforeSendTransaction`.
          // enableStandaloneClsSpans: true,
        },
      }),
      Sentry.httpClientIntegration({
        // we handle the network errors globally with an interceptor
        failedRequestStatusCodes: [],
      }),
      Sentry.replayIntegration({
        // Additional SDK configuration goes in here, for example:
        maskAllText: false,
        maskAllInputs: false,
        blockAllMedia: false,
        _experiments: {
          captureExceptions: config.sentry?._experiments?.captureExceptions ?? false,
          traceInternals: config.sentry?._experiments?.traceInternals ?? false
        },
        stickySession: true,
        networkDetailAllowUrls: [
          new URL(config.endpoints.api).host,
          new URL(config.endpoints.api).origin,
          new URL(config.endpoints.mainSite).host,
          new URL(config.endpoints.mainSite).origin,
          window.location.origin,
          window.location.host
        ],
        networkRequestHeaders: [
          'Authorization',
          'Content-Type',
          'Accept',
          'If-Match',
          'If-None-Match',
          'If-Modified-Since',
          'If-Unmodified-Since',
          'If-Range',
          'Cache-Control',
          'X-Piwik-Session',
          'X-Referring-Source',
          'X-Referring-Source-Entry-Url',

        ],
        networkResponseHeaders: [
          'Content-Type',
          'Content-Length',
          'Content-Encoding',
          'Content-Disposition',
          'Location',
          'ETag',
          'Last-Modified',
          'Cache-Control',
          'Referrer-Policy',
        ],
      }),
    ],
    _experiments: {},
    debug: config.sentry?.debug ?? false,
    normalizeDepth: 8,
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions


    tracePropagationTargets: [
      new URL(config.endpoints.api).host,
      new URL(config.endpoints.api).origin,
      new URL(config.endpoints.mainSite).host,
      new URL(config.endpoints.mainSite).origin,
      window.location.origin,
      window.location.host
    ],

    // Session Replay
    replaysSessionSampleRate: config.sentry?.replaysSessionSampleRate ?? 1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.

    sendDefaultPii: config.sentry?.sendDefaultPii ?? true,
  });
}
